import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Mail, Trash2 } from 'lucide-react';
import { useAuth } from '@pooflabs/web';
import { getIdToken } from '@pooflabs/web';
import { useAppTheme } from '@/utils/theme-context';
import { useRealtimeData } from '@/hooks/use-realtime-data';
import { subscribeUserProfiles, UserProfilesResponse } from '@/lib/collections/userProfiles';
import { createAuthenticatedApiClient } from '@/lib/api-client';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';

/**
 * The inner body of the Settings screen — no page shell, no back button.
 * Used both by SettingsPage (full-screen route) and the inline accordion in MenuPanel.
 */
export function SettingsContent() {
  const { tokens } = useAppTheme();
  const { user } = useAuth();
  const navigate = useNavigate();

  const [deleting, setDeleting] = useState(false);

  // Subscribe to the current user's profile — used to gate the delete row
  const { data: userProfile } = useRealtimeData<UserProfilesResponse | null>(
    subscribeUserProfiles,
    !!user?.address,
    user?.address ?? '',
  );

  const hasProfile = !!user && userProfile !== null && userProfile !== undefined;

  const handleDeleteProfile = async () => {
    if (!user) return;
    setDeleting(true);
    try {
      const token = await getIdToken();
      if (!token) {
        toast.error('Authentication error. Please reconnect your wallet.');
        return;
      }
      const authApi = createAuthenticatedApiClient(token, user.address);
      await authApi.delete('/api/profile');
      navigate('/');
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to delete profile';
      toast.error(msg);
    } finally {
      setDeleting(false);
    }
  };

  // Nothing to show when the user has no author profile — render nothing
  // (avoids an empty padded block in the Settings accordion / page).
  if (!hasProfile) return null;

  return (
    <div style={{ padding: '14px 16px 20px' }}>

      {/* Contact Support */}
      <a
        href="mailto:isupport@mailens.xyz"
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          width: '100%',
          background: 'transparent',
          borderRadius: 8,
          padding: '9px 10px',
          textDecoration: 'none',
          color: tokens.textMuted,
          marginBottom: 2,
          transition: 'background 0.15s',
        }}
        onMouseEnter={e => {
          (e.currentTarget as HTMLAnchorElement).style.background = 'rgba(255,255,255,0.06)';
        }}
        onMouseLeave={e => {
          (e.currentTarget as HTMLAnchorElement).style.background = 'transparent';
        }}
      >
        <Mail size={15} strokeWidth={2} />
        <span style={{
          fontSize: 13,
          fontWeight: 500,
          fontFamily: "'Outfit', sans-serif",
        }}>
          Contact Support
        </span>
      </a>

      {/* Delete creator profile — only visible when the user has a profile */}
      {(
        <>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <button
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  width: '100%',
                  background: 'transparent',
                  border: 'none',
                  borderRadius: 8,
                  padding: '9px 10px',
                  cursor: deleting ? 'not-allowed' : 'pointer',
                  color: '#ef4444',
                  textAlign: 'left',
                  opacity: deleting ? 0.5 : 1,
                  transition: 'background 0.15s',
                }}
                disabled={deleting}
                onMouseEnter={e => {
                  (e.currentTarget as HTMLButtonElement).style.background = 'rgba(239,68,68,0.08)';
                }}
                onMouseLeave={e => {
                  (e.currentTarget as HTMLButtonElement).style.background = 'transparent';
                }}
              >
                <Trash2 size={15} strokeWidth={2} />
                <span style={{
                  fontSize: 13,
                  fontWeight: 500,
                  fontFamily: "'Outfit', sans-serif",
                }}>
                  {deleting ? 'Deleting…' : 'Delete Creator Profile'}
                </span>
              </button>
            </AlertDialogTrigger>

            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Delete Creator Profile?</AlertDialogTitle>
                <AlertDialogDescription>
                  This action cannot be undone. Your creator profile, avatar, nickname, and all
                  subscriptions will be permanently removed. Your uploaded wallpapers will be
                  archived. Purchases made by buyers are preserved.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  onClick={handleDeleteProfile}
                  style={{ background: '#ef4444', color: '#fff' }}
                >
                  Delete Profile
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </>
      )}
    </div>
  );
}
